// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import googlelibphonenumber from 'google-libphonenumber'
import { z } from 'zod'
import isEmail from 'validator/lib/isEmail'
import styled from 'styled-components'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, Controller } from 'react-hook-form'
import Grid from '@material-ui/core/Grid'
import { FormControl } from '@material-ui/core'
import TypographyPro from 'themes/TypographyPro'
import { respondAbove } from 'styles/mixins'
import useWindowState from 'hooks/useWindowState'
import ButtonBase from 'components/common/ButtonBase'
import TextField from 'components/common/TextField'
import { getTranslatedTextByKey } from 'utils/utils'

const nameRegex = /^[\p{L} -]+$/u

const { PhoneNumberType } = googlelibphonenumber
const phoneUtil = googlelibphonenumber.PhoneNumberUtil.getInstance()

const Edit = styled.button`
	border: none;
	background: transparent;
	align-self: flex-start;
	cursor: pointer;
	color: var(--inactive);
	padding-inline-start: 20px;
	padding-inline-end: 0;
`

const StyledSectionBody = styled(Grid)`
	display: flex;
	margin-top: 15px;

	${respondAbove.md`
		margin-top: 21px;
	`}
`

const StyledLabel = styled(TypographyPro)`
	padding-bottom: 3px;
	color: var(--fonts);
	flex: 1;
`

const SaveButton = styled(ButtonBase)`
	width: 162px;
	margin: 0 auto;
`

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	row-gap: 20px;
	margin-top: 20px;
`

const FullNameAndPhoneNumberContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 20px;
`

const InputContainer = styled.div`
	flex: 1;
	width: 100%;
	display: flex;
	justify-content: flex-start;
	align-items: flex-start;
	align-self: flex-start;
	flex-direction: column;
`

const UserDetailsContainer = styled.div`
	display: table;
	table-layout: fixed;
	width: 100%;
`

const Text = styled(TypographyPro)`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const errorKeysMap = {
	'email.alreadyTaken': 'emailAlreadyTakenError',
	'phone.alreadyTaken': 'phoneAlreadyTakenError',
}

const errorCodeToFieldNameMap = {
	emailAlreadyTakenError: 'email',
	phoneAlreadyTakenError: 'phoneNumber',
}

export const buildUserDetailsSchema = (defaultCountry) =>
	z.object({
		fullName: z.string().min(2).max(50).regex(nameRegex),
		phoneNumber: z.string().refine(
			(value) => {
				try {
					const _phoneNumber = phoneUtil.parse(value, defaultCountry)
					if (phoneUtil.isValidNumber(_phoneNumber)) {
						if (
							phoneUtil.getNumberType(_phoneNumber) === PhoneNumberType.MOBILE ||
							phoneUtil.getNumberType(_phoneNumber) === PhoneNumberType.FIXED_LINE_OR_MOBILE
						) {
							return true
						}
					}
					return false
				} catch {
					return false
				}
			},
			{ message: getTranslatedTextByKey('eCommerce.signIn.invalidMobileNumber') }
		),
		email: z
			.string()
			.trim()
			.min(1, { message: `${getTranslatedTextByKey('webviewFlow.cannotBeEmpty', 'Cannot be empty')}` })
			.refine((val) => isEmail(val, { require_tld: true }), {
				message: `${getTranslatedTextByKey('webviewFlow.mustBeValidEmail', 'Must be a valid email')}`,
			}),
	})

const UserDetails = inject(
	'Account',
	'Infra',
	'Application'
)(
	observer(({ Account, Infra, Application }) => {
		const [isEdit, setIsEdit] = useState(false)
		const {
			personalInfo: { firstName, lastName, email, phone },
			storeUserDetails,
			defaultCountry,
			fieldsAttributes,
		} = Account
		const [addState] = useWindowState()

		const {
			reset,
			handleSubmit,
			formState: { errors, isDirty, isValid },
			setError,
			clearErrors,
			control,
		} = useForm({
			mode: 'onChange',
			resolver: zodResolver(buildUserDetailsSchema(defaultCountry)),
		})

		useEffect(() => {
			if (isEdit) {
				reset({ phoneNumber: phone, email })
			}
		}, [isEdit])

		useEffect(() => {
			if (firstName && phone && email) {
				reset({ fullName: `${firstName} ${lastName}`.trim(), phoneNumber: phone, email })
			}
		}, [firstName, lastName, phone, email])

		const onEditHandler = () => {
			addState(() => {
				setIsEdit(false)
			})
			setIsEdit(true)
		}

		const renderUserDetails = () => (
			<StyledSectionBody>
				<UserDetailsContainer data-testid="user-details-container">
					{(firstName || lastName) && <Text variant="BodyRegular" data-testid="user-name">{`${firstName} ${lastName}`}</Text>}
					{email && (
						<Text variant="BodyRegular" data-testid="user-email">
							{email}
						</Text>
					)}
					{phone && (
						<Text variant="BodyRegular" data-testid="user-phone">
							{phone}
						</Text>
					)}
				</UserDetailsContainer>
				<Edit onClick={onEditHandler}>
					<TypographyPro variant="BodyRegularClickable" data-testid="account-edit-user-info-button">
						{getTranslatedTextByKey('btns.edit')}
					</TypographyPro>
				</Edit>
			</StyledSectionBody>
		)

		const onSubmit = async ({ fullName: submittedFullName, email: submittedEmail, phoneNumber: submittedPhoneNumber }) => {
			try {
				Infra.setLoading(true)
				await storeUserDetails(Application.backendChannel, submittedFullName.trim(), submittedEmail, submittedPhoneNumber)
				setIsEdit(false)
			} catch (error) {
				if (error.response?.status === 409) {
					const errorKey = errorKeysMap[error.response?.data?.error?.code]
					const errorField = errorCodeToFieldNameMap[errorKey]
					if (errorField) {
						setError(errorField, {
							message: getTranslatedTextByKey(`webviewFlow.${errorKey}`, error.response?.data?.error?.message),
						})
					}
				} else {
					clearErrors()
					Infra.setErrorNotification(getTranslatedTextByKey('webviewFlow.serverErrorMessage'), true, true)
				}
			} finally {
				Infra.setLoading(false)
			}
		}

		const renderUserDetailsEdit = () => (
			<StyledForm onSubmit={handleSubmit(onSubmit)}>
				<FullNameAndPhoneNumberContainer data-testid="user-name-and-phone-input">
					<InputContainer>
						<StyledLabel variant="BodyRegularHighlighted">{getTranslatedTextByKey('eCommerce.signIn.fullName')}*</StyledLabel>
						<Controller
							name="fullName"
							control={control}
							render={({ field: { onChange, value } }) => (
								<TextField
									onChange={onChange}
									value={value ?? ''}
									error={Object.hasOwn(errors, 'fullName')}
									placeholder={getTranslatedTextByKey('webviewFlow.nameFormPlaceholder')}
									errorMessage={getTranslatedTextByKey('webviewFlow.firstNameError')}
									inputProps={{
										'data-testid': 'user-name-field',
									}}
								/>
								// <StyledOutlinedInput
								// 	onChange={onChange}
								// 	value={value}
								// 	error={Object.prototype.hasOwnProperty.call(errors, 'fullName')}
								// 	placeholder={User.userLocaleLanguage.webviewFlow.nameFormPlaceholder}
								// />
							)}
						/>
					</InputContainer>

					<InputContainer>
						<StyledLabel variant="BodyRegularHighlighted">{getTranslatedTextByKey('eCommerce.signIn.phoneNumber')}*</StyledLabel>
						<Controller
							name="phoneNumber"
							control={control}
							render={({ field: { onChange, value } }) => (
								<TextField
									onChange={onChange}
									value={value}
									error={Object.hasOwn(errors, 'phoneNumber')}
									placeholder={getTranslatedTextByKey('webviewFlow.phoneFormPlaceholder')}
									errorMessage={errors.phoneNumber?.message || getTranslatedTextByKey('eCommerce.signIn.invalidMobileNumber')}
									inputProps={{
										'data-testid': 'phone-field',
									}}
									disabled={fieldsAttributes?.phone?.blocked}
								/>
								// <StyledOutlinedInput
								// 	onChange={onChange}
								// 	value={value}
								// 	error={Object.prototype.hasOwnProperty.call(errors, 'phoneNumber')}
								// 	placeholder={User.userLocaleLanguage.webviewFlow.phoneFormPlaceholder}
								// />
							)}
						/>
					</InputContainer>
				</FullNameAndPhoneNumberContainer>
				<FormControl>
					<StyledLabel variant="BodyRegularHighlighted">{getTranslatedTextByKey('eCommerce.signIn.email')}*</StyledLabel>
					<Controller
						name="email"
						control={control}
						render={({ field: { onChange, value } }) => (
							<TextField
								onChange={onChange}
								value={value}
								error={Object.hasOwn(errors, 'email')}
								placeholder={getTranslatedTextByKey('webviewFlow.emailFormPlaceholder')}
								errorMessage={errors.email?.message || getTranslatedTextByKey('webviewFlow.mustBeValidEmail')}
								inputProps={{
									'data-testid': 'email-field',
								}}
								disabled={fieldsAttributes?.email?.blocked}
							/>
							// <StyledOutlinedInput
							// 	onChange={onChange}
							// 	value={value}
							// 	error={Object.prototype.hasOwnProperty.call(errors, 'email')}
							// 	placeholder={User.userLocaleLanguage.webviewFlow.emailFormPlaceholder}
							// 	$fullWidth
							// />
						)}
					/>
				</FormControl>
				<SaveButton disabled={!isDirty || !isValid} type="submit" data-testid="user-details-save-button">
					{getTranslatedTextByKey('eCommerce.accountSettings.save')}
				</SaveButton>
			</StyledForm>
		)

		return isEdit ? renderUserDetailsEdit() : renderUserDetails()
	})
)

export default UserDetails
